import React, { useState, useEffect } from "react";
import UserModel from "./types";

interface PersonalInfoSectionProps {
  profileData: UserModel;
  isEditing: boolean;
  onUpdate: (data: Partial<UserModel>) => Promise<void>;
  isSaving: boolean;
}

const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  profileData,
  isEditing,
  onUpdate,
  isSaving,
}) => {
  const [formData, setFormData] = useState({
    firstName: profileData.firstName,
    lastName: profileData.lastName,
    phone: profileData.phone,
    birthDate: profileData.birthDate,
  });

  // Reset form data when profileData changes or when exiting edit mode
  useEffect(() => {
    setFormData({
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      phone: profileData.phone,
      birthDate: profileData.birthDate,
    });
  }, [profileData, isEditing]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate(formData);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-semibold mb-4">Personal Information</h3>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* First Name */}
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-300 mb-1"
            >
              First Name
            </label>
            {isEditing ? (
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 text-white"
                disabled={isSaving}
                required
              />
            ) : (
              <p className="py-2 text-white">{profileData.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-300 mb-1"
            >
              Last Name
            </label>
            {isEditing ? (
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 text-white"
                disabled={isSaving}
                required
              />
            ) : (
              <p className="py-2 text-white">{profileData.lastName}</p>
            )}
          </div>

          {/* Email (non-editable) */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-300 mb-1"
            >
              Email Address
            </label>
            <p className="py-2 text-white">{profileData.email}</p>
          </div>

          {/* Phone */}
          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-300 mb-1"
            >
              Phone Number
            </label>
            {isEditing ? (
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 text-white"
                disabled={isSaving}
                required
              />
            ) : (
              <p className="py-2 text-white">{profileData.phone}</p>
            )}
          </div>

          {/* Date of Birth */}
          <div>
            <label
              htmlFor="birthDate"
              className="block text-sm font-medium text-gray-300 mb-1"
            >
              Date of Birth
            </label>
            {isEditing ? (
              <input
                id="birthDate"
                name="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-cannabis-green-300 focus:border-cannabis-green-500 text-white"
                disabled={isSaving}
                required
              />
            ) : (
              <p className="py-2 text-white">
                {new Date(profileData.birthDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>

        {isEditing && (
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-cannabis-green-500 hover:bg-cannabis-green-600 rounded-lg transition flex items-center"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default PersonalInfoSection;
